import { useState, useRef, useCallback, useEffect } from "react";
import { Camera, RotateCcw, Check, Upload, X, SunMedium, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Guided camera capture for selfie / IC front / IC back.
 *
 * IMPORTANT: This performs ONLY basic pixel-level heuristics (average
 * brightness + a cheap blur estimate). It does NOT perform face detection,
 * document detection, liveness detection or any identity verification.
 * These are non-AI quality hints only, to help the applicant retake a
 * clearly bad photo before submitting - they never block submission.
 */

const MODE_CONFIG = {
  selfie: {
    label: "Gambar Diri",
    facingMode: "user",
    guideShape: "oval",
    instructions: "Letakkan wajah anda di dalam bulatan. Pastikan pencahayaan cukup terang.",
  },
  ic_front: {
    label: "Kad Pengenalan (Depan)",
    facingMode: "environment",
    guideShape: "card",
    instructions: "Letakkan bahagian HADAPAN kad pengenalan anda di dalam bingkai.",
  },
  ic_back: {
    label: "Kad Pengenalan (Belakang)",
    facingMode: "environment",
    guideShape: "card",
    instructions: "Letakkan bahagian BELAKANG kad pengenalan anda di dalam bingkai.",
  },
};

function analyzeFrame(canvas) {
  const ctx = canvas.getContext("2d");
  const w = 80, h = Math.round((80 * canvas.height) / canvas.width);
  const small = document.createElement("canvas");
  small.width = w;
  small.height = h;
  const sctx = small.getContext("2d");
  sctx.drawImage(canvas, 0, 0, w, h);
  const { data } = sctx.getImageData(0, 0, w, h);
  const gray = new Float32Array(w * h);
  let sum = 0;
  for (let i = 0; i < w * h; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
    const v = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = v;
    sum += v;
  }
  const brightness = sum / (w * h);
  let diffSum = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x;
      const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w];
      diffSum += Math.abs(lap);
    }
  }
  const blurScore = diffSum / (w * h);
  return {
    brightness,
    blurScore,
    tooDark: brightness < 60,
    tooBright: brightness > 220,
    tooBlurry: blurScore < 6,
  };
}

export function GuidedCameraCapture({ mode, onCapture, onCancel }) {
  const config = MODE_CONFIG[mode];
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [captured, setCaptured] = useState(null);
  const [quality, setQuality] = useState(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const startCamera = async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: config.facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err) {
      setCameraError("Tidak dapat mengakses kamera. Sila benarkan akses kamera atau muat naik fail secara manual.");
    }
  };

  const takeShot = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setQuality(analyzeFrame(canvas));
    setCaptured(canvas.toDataURL("image/jpeg", 0.92));
    stopStream();
  };

  const retake = () => {
    setCaptured(null);
    setQuality(null);
    startCamera();
  };

  const accept = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      onCapture(blob, `${mode}.jpg`);
    }, "image/jpeg", 0.92);
  };

  const handleFileFallback = (e) => {
    const file = e.target.files?.[0];
    if (file) onCapture(file, file.name);
  };

  return (
    <div className="rounded-xl border border-piks-border bg-piks-ivory p-4" data-testid={`camera-capture-${mode}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-piks-black">{config.label}</p>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid={`camera-cancel-${mode}`} className="text-piks-muted hover:text-piks-black">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {!cameraActive && !captured && (
        <div className="text-center py-6">
          <p className="text-xs text-piks-muted mb-4">{config.instructions}</p>
          {cameraError && <p className="text-xs text-piks-red mb-3" data-testid={`camera-error-${mode}`}>{cameraError}</p>}
          <Button type="button" onClick={startCamera} data-testid={`camera-start-${mode}`} className="bg-piks-black hover:bg-piks-black/90 mb-3">
            <Camera className="w-4 h-4 mr-1.5" /> Guna Kamera
          </Button>
          <div>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-piks-border cursor-pointer text-sm hover:bg-white">
              <Upload className="w-4 h-4" /> Muat Naik Fail Sebaliknya
              <input type="file" accept="image/*" className="hidden" data-testid={`camera-fallback-upload-${mode}`} onChange={handleFileFallback} />
            </label>
          </div>
        </div>
      )}

      {cameraActive && (
        <div>
          <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "4/3" }}>
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {config.guideShape === "oval" ? (
                <div className="w-40 h-52 border-4 border-white/80 rounded-[50%] shadow-[0_0_0_2000px_rgba(0,0,0,0.35)]" />
              ) : (
                <div className="w-[85%] border-4 border-white/80 rounded-xl shadow-[0_0_0_2000px_rgba(0,0,0,0.35)]" style={{ aspectRatio: "1.586/1" }} />
              )}
            </div>
          </div>
          <p className="text-xs text-piks-muted text-center mt-2">{config.instructions}</p>
          <div className="flex justify-center gap-3 mt-3">
            <Button type="button" variant="outline" onClick={stopStream} data-testid={`camera-stop-${mode}`}>Batal</Button>
            <Button type="button" onClick={takeShot} data-testid={`camera-shoot-${mode}`} className="bg-piks-red hover:bg-piks-red/90">
              <Camera className="w-4 h-4 mr-1.5" /> Tangkap Gambar
            </Button>
          </div>
        </div>
      )}

      {captured && (
        <div>
          <img src={captured} alt="Pratonton" className="w-full rounded-lg border border-piks-border" data-testid={`camera-preview-${mode}`} />
          {quality && (quality.tooDark || quality.tooBright || quality.tooBlurry) && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`camera-quality-warning-${mode}`}>
              {quality.tooBlurry ? <ScanLine className="w-4 h-4 shrink-0 mt-0.5" /> : <SunMedium className="w-4 h-4 shrink-0 mt-0.5" />}
              <span>
                {quality.tooDark && "Gambar mungkin terlalu gelap. "}
                {quality.tooBright && "Gambar mungkin terlalu terang. "}
                {quality.tooBlurry && "Gambar mungkin kabur. "}
                Anda masih boleh gunakan gambar ini, atau tekan "Ambil Semula" untuk cuba lagi.
              </span>
            </div>
          )}
          <div className="flex justify-center gap-3 mt-3">
            <Button type="button" variant="outline" onClick={retake} data-testid={`camera-retake-${mode}`}>
              <RotateCcw className="w-4 h-4 mr-1.5" /> Ambil Semula
            </Button>
            <Button type="button" onClick={accept} data-testid={`camera-accept-${mode}`} className="bg-piks-black hover:bg-piks-black/90">
              <Check className="w-4 h-4 mr-1.5" /> Guna Gambar Ini
            </Button>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
